import { toTypedSchema } from '@vee-validate/zod';
import * as changeCase from 'change-case';
import { defineStore } from 'pinia';
import { useForm } from 'vee-validate';
import * as zod from 'zod';

import type { Claim, ClaimLineItem, Deduction } from '@stuut/api/models';
import { toast } from '@ui/components/sonner';

import { stuut } from '@/api';

const lineItemValidationSchema = toTypedSchema(
  zod.object({
    external_name: zod.string().optional().nullable(),
    fee: zod.union([zod.coerce.number(), zod.string()]).nullable(),
    item_id: zod.string().optional().nullable(),
    price: zod.union([zod.coerce.number(), zod.string()]).nullable(),
    quantity: zod.union([zod.coerce.number(), zod.string()]).nullable(),
    reason_id: zod.string().optional().nullable(),
    sku: zod.string().nullable(),
    total_amount: zod.union([zod.coerce.number(), zod.string()]).nullable(),
    upc: zod.string().nullable(),
    vendor_id: zod.string().optional().nullable(),
  }),
);

export interface FormClaimLineItem {
  formData: any;
  id: string;
  isDeleted?: boolean;
  isDeleting?: boolean;
  isDisabled?: boolean;
  isEditing?: boolean;
  isSaving?: boolean;
  lineItem: ClaimLineItem;
}

interface State {
  claimLineItems: FormClaimLineItem[];
  deletedClaimLineItems: FormClaimLineItem[];
  isEditDisabled: boolean;
}

export const useClaimLineItemStore = defineStore('claimLineItems', {
  actions: {
    createFormClaimLineItem(lineItem: ClaimLineItem) {
      return {
        formData: this.createFormData(lineItem),
        id: lineItem?.id,
        isEditing: false,
        lineItem,
      };
    },

    createFormData(claimLineItem: ClaimLineItem) {
      return useForm({
        initialValues: {
          external_name: claimLineItem.external_name,
          fee: claimLineItem.fee,
          item_id: claimLineItem.item_id,
          price: claimLineItem.price,
          quantity: claimLineItem.quantity,
          reason_id: claimLineItem.reason_id,
          sku: claimLineItem.sku,
          total_amount: claimLineItem.total_amount,
          upc: claimLineItem.upc,
          vendor_id: claimLineItem.vendor_id,
        },
        validationSchema: lineItemValidationSchema,
      });
    },

    async createLineItem(claimId: string, data?: Partial<ClaimLineItem>) {
      if (!claimId) return;

      try {
        const lineItem = await stuut.claimLineItems.create({
          claim_id: claimId,
          ...(data || {}),
        });

        const formLineItem = this.createFormClaimLineItem(lineItem);

        this.claimLineItems = [...this.claimLineItems, formLineItem];
      } catch (err: any) {
        toast.error(err.clientMessage || err.message);
      }
    },

    async deleteItem(row) {
      row.original.isDeleting = true;

      try {
        const response = await stuut.claimLineItems.delete(row.id);
        row.original.isDeleted = true;

        this.removeRow(row.id);

        return response;
      } finally {
        row.original.isDeleting = false;
      }
    },

    discardEdit(row) {
      const claimLineItem = this.getById(row.id);

      if (!claimLineItem?.id) return;

      claimLineItem.formData.handleReset();
      claimLineItem.isEditing = false;

      return claimLineItem;
    },

    endEdit(id: string) {
      const claimLineItem = this.getById(id);

      if (!claimLineItem?.id) return;

      claimLineItem.isEditing = false;

      return claimLineItem;
    },

    removeRow(id) {
      const index = this.claimLineItems.findIndex((item) => item.id === id);

      this.claimLineItems = [
        ...(this.claimLineItems?.slice(0, index) || []),
        ...(this.claimLineItems?.slice(index + 1) || []),
      ];

      return this.claimLineItems;
    },

    async saveEdit(row) {
      const claimLineItem = this.getById(row.id);

      if (!claimLineItem?.id) return;

      const validationResult = await row.original.formData.validate();

      if (!validationResult.valid) {
        const invalidFields = Object.keys(validationResult.results)
          .filter((key) => validationResult?.results?.[key]?.valid === false)
          .map((field) => changeCase.sentenceCase(field));

        throw Error(`Fix these fields and retry: ${invalidFields.join(', ')}`);
      }

      const response = await stuut.claimLineItems.update(
        claimLineItem.id,
        claimLineItem.formData.values,
        claimLineItem.lineItem,
      );

      this.updateRow(response);
      this.endEdit(claimLineItem.id);

      return response;
    },

    async setup(claim: Claim | Deduction) {
      const claimLineItems = claim.line_items;

      if (!claimLineItems?.length) return;

      this.setupData(claimLineItems);

      return this.claimLineItems;
    },

    setupData(items: ClaimLineItem[]) {
      const claimLineItems = items?.map((lineItem) => {
        return this.createFormClaimLineItem(lineItem);
      });

      this.claimLineItems = claimLineItems;
    },

    startEdit(id: string) {
      const claimLineItem = this.getById(id);

      if (!claimLineItem?.id) return;

      claimLineItem.isEditing = true;

      return claimLineItem;
    },

    undoDelete(row: any) {},

    updateRow(lineItem: ClaimLineItem) {
      const index = this.claimLineItems.findIndex((item) => item.id === lineItem.id);
      const existingItem = this.claimLineItems[index];

      if (index > -1) {
        const newItems = [...this.claimLineItems];

        newItems[index] = this.createFormClaimLineItem(lineItem);

        this.$patch({ claimLineItems: newItems });
      }
    },
  },

  getters: {
    getById: (state) => {
      return (id: string) => state.claimLineItems.find((item) => item.id === id);
    },
  },

  state: (): State => ({
    claimLineItems: [],
    deletedClaimLineItems: [],
    isEditDisabled: false,
  }),
});

export default useClaimLineItemStore;
